import React, { useState } from 'react';

import { TIMELINE_YEAR_REVEAL_DEFAULT, TIMELINE_YEAR_REVEAL_MAX } from '../../constants/patientCard';
import TimelineRow from './TimelineRow';

/**
 * PatientTimelineYearGroup (F6, R10 long-list) — one year's rows, collapsed
 * to a count when not the default-open group, and revealing at most
 * `TIMELINE_YEAR_REVEAL_DEFAULT` (20) rows at a time via "Show N more", never
 * more than `TIMELINE_YEAR_REVEAL_MAX` (50) rows rendered in one group even
 * across repeated clicks — the render stays bounded regardless of how large
 * a single year's history gets.
 *
 * `rows` must already be sorted (descending `occurred_at`) and type-filtered
 * by the caller — this component only reveals/collapses, it does not sort or
 * filter.
 *
 * `sections` is passed straight through to `TimelineRow` (which hands it to
 * F7 / F8 for their expanded detail) — this component never reads it.
 */
export default function PatientTimelineYearGroup({ year, rows, defaultOpen, sections }) {
  const [open, setOpen] = useState(defaultOpen);
  const [visibleCount, setVisibleCount] = useState(
    Math.min(rows.length, TIMELINE_YEAR_REVEAL_DEFAULT),
  );

  const total = rows.length;
  const visibleRows = rows.slice(0, visibleCount);
  const remaining = total - visibleCount;
  const canShowMore = remaining > 0 && visibleCount < TIMELINE_YEAR_REVEAL_MAX;
  const nextReveal = Math.min(
    TIMELINE_YEAR_REVEAL_DEFAULT,
    remaining,
    TIMELINE_YEAR_REVEAL_MAX - visibleCount,
  );

  return (
    <div
      data-testid={`timeline-year-${year}`}
      className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden mb-2"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`timeline-year-toggle-${year}`}
        className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 text-sm font-bold text-slate-800 dark:text-slate-100"
      >
        <span>
          {year}{' '}
          <span className="font-semibold text-slate-500 dark:text-slate-400">
            · {total} record{total === 1 ? '' : 's'}
          </span>
        </span>
        <span className={`text-xs text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>
      {open && (
        <div className="px-3 py-3 space-y-2">
          {visibleRows.map((row) => (
            <TimelineRow key={`${row.ref.kind}-${row.ref.id}`} row={row} sections={sections} />
          ))}
          {canShowMore && (
            <button
              type="button"
              onClick={() => setVisibleCount((c) => Math.min(c + TIMELINE_YEAR_REVEAL_DEFAULT, TIMELINE_YEAR_REVEAL_MAX, total))}
              data-testid={`timeline-year-showmore-${year}`}
              className="text-xs font-semibold text-teal-700 dark:text-teal-300 hover:underline"
            >
              Show {nextReveal} more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
